import { createSelector } from 'reselect';

import { connectionsTypes } from '../constants';

import { isInCurrentPeriodSelector, userSelector } from '../../shared/store/selectors';
import { withCTTDescription } from '../../shared/store/utils';
import { decodeCountryTaxType } from '../../utils/encodeDecodeCTT';

import {
  userRoles,
  assignmentTypes,
  checkAccessBasedOnAssignmentPermissions,
} from '../../shared/authorization';
import { NO_FLAG } from '../../shared/constants';

export const countryTaxTypesOptionsSelector = createSelector(
  state => state.provision.general.countryTaxTypes,
  countryTaxTypes => countryTaxTypes.map(({ countryTaxType, description, subdivision }) => ({
    value: countryTaxType,
    label: description,
    subdivision,
  }))
);

export const hasUploadTrialBalancePrivilegeSelector = createSelector(
  ({ shared }) => shared.period.name === shared.currentPeriod.name,
  ({ user }) => user.user.role === userRoles.SUPER_USER,
  (isInCurrentPeriod, isSuperUser ) => (isInCurrentPeriod || isSuperUser)
);

export const cannotUploadTrialBalanceSelector = createSelector(
  hasUploadTrialBalancePrivilegeSelector,
  ({ shared }) => shared.companyCodes.length
    ? shared.companyCodes.find(({ cc }) => cc === shared.companyCode)
    : null,
  (hasPrivilege, companyCode) =>
    (!companyCode || !!companyCode.connection || companyCode.enabledObligations === NO_FLAG)
    || !hasPrivilege
);

export const cannotCalculateTrialBalanceSelector = createSelector(
  cannotUploadTrialBalanceSelector,
  ({ provision }) => provision.trialBalance.trialBalanceCollection,
  ({ provision }) => provision.trialBalance.trialBalanceStatus,
  (
    cannotCalculateOrUpdateTrialBalance,
    trialBalanceCollection,
    trialBalanceStatus
  ) => cannotCalculateOrUpdateTrialBalance || !trialBalanceCollection.length || trialBalanceStatus.importStatus === -1
);

export const cannotDownloadCompanyDetailsSelector = createSelector(
  ({ provision }) => provision.general.statusName,
  ({ shared }) => shared.companyCodes.length
    ? shared.companyCodes.find(({ cc }) => cc === shared.companyCode)
    : null,
  ({ shared }) => shared.fetchingInitialProvisionData,
  (statusName, companyCode, fetchingInitialProvisionData) => (
    !companyCode || fetchingInitialProvisionData || !statusName
    || (statusName.toLowerCase() !== 'calculated' && statusName.toLowerCase() !== 'updated')
  )
);

export const adjustmentTypesSelector = createSelector(
  state => state.provision.general.adjustmentTypes,
  adjustmentTypes => adjustmentTypes.map(({ value, display, bookType }) => ({
    value,
    label: display,
    bookType,
  }))
);

const checkPermissions = (user, connection) => {
  const assignmentToCheck = {
    assignmentType: assignmentTypes.CN,
    requiredPermission: null,
  };

  return connection.connType === connectionsTypes.FileUI
  || connection.connType === connectionsTypes.FileFTP
  || checkAccessBasedOnAssignmentPermissions({
    user,
    companyCode: connection.connName,
    withAssignments: [assignmentToCheck],
  });
};

export const connectionsOptionsSelector = createSelector(
  [
    state => state.provision.batchProvisions.connections,
    state => state.user.user,
  ],
  (connections, user) => connections
    .filter(connection => checkPermissions(user, connection))
    .map(({ connName }) => ({
      label: connName,
      value: connName,
    }))
);

export const connectionSelector = state => state.provision.batchProvisions.connection;

export const isGlUIConnectionSelectedSelector = state =>
  state.provision.batchProvisions.connection
  && state.provision.batchProvisions.connection.connType === connectionsTypes.GlUI;

export const hasAccessSelector = createSelector(
  isInCurrentPeriodSelector,
  (state, hasAccessBasedOnAssignments) => hasAccessBasedOnAssignments,
  ({ user }) => user.user.role === userRoles.SUPER_USER,
  (isInCurrentPeriod, hasAccessBasedOnAssignments, isSuperUser) =>
    hasAccessBasedOnAssignments && (isInCurrentPeriod || isSuperUser)
);

export const canCalculateProformaProvisionSelector = createSelector(
  userSelector,
  user => user.permissions.commands.canCalculateProformaProvision
);

export const rawAdjustmentsSelector = ({ provision }) => provision.adjustments;
export const rawAdjustmentsCollectionSelector = ({ provision }) => provision.adjustments.adjustmentsCollection;
export const adjustmentsCollectionSelector = createSelector(
  withCTTDescription(rawAdjustmentsCollectionSelector),
  adjustments => adjustments.map(({ countryTaxType, ...rest }) => ({
    ...rest,
    ...decodeCountryTaxType(countryTaxType),
  }))
);

export const rawBookToTaxDifferencesSelector = ({ provision }) => provision.adjustments.bookToTaxDifferencesCollection;

export const adjustmentsSelector = createSelector(
  [
    adjustmentsCollectionSelector,
    withCTTDescription(rawBookToTaxDifferencesSelector),
  ],
  (adjustmentsCollection, bookToTaxDifferencesCollection) => ({
    adjustmentsCollection,
    bookToTaxDifferencesCollection,
  })
);

export const statCalcSelector = ({ provision }) => provision.general.statCalc;

export const isDownloadingProvisionDetailsSelector = ({ provision }) => provision.general.isDownloadingProvisionDetails;
